import { useCallback, useEffect, useRef, type ReactElement } from "react";
import { Plus } from "lucide-react";
import { useRovingTabIndex } from "../primitives/index.js";
import { useListPresence } from "../hooks/useListPresence.js";
import { Tooltip } from "../molecules/index.js";
import { ChromeIconButton } from "./ChromeIconButton.js";
import { TabStripItem } from "./TabStripItem.js";
import type { TabStripProps } from "./tabs.types.js";

/** Matches the scroller's own `scroll-px-2`, so a revealed tab keeps its base
    flares — drawn 8px outside its box — clear of the clipped edge. */
const REVEAL_PADDING = 8;

/**
 * Bring a whole tab SLOT into view along the strip — not just its route button,
 * whose box stops short of the close and kebab controls, which would stay
 * clipped at the edge.
 *
 * The inline scroll is computed here rather than handed to `scrollIntoView`,
 * because that call cannot be told to leave an axis alone. `block: "nearest"`
 * does not mean "never scroll vertically": it scrolls whenever the element is
 * not fully visible in the block axis, and it will do that to an
 * `overflow-y: hidden` port — `hidden` still builds a scroll container that
 * script can move, only `clip` does not. This strip is that port, `items-end`
 * leaves its tabs flush with the bottom edge, and so a 4px arrival nudge was
 * enough: one call scrolled the strip 4px, and the browser then clamped it back
 * down frame by frame as the transform decayed and the overflow shrank. The
 * whole strip slid up and settled on every new tab. One axis, computed; the
 * other is never touched.
 *
 * `overflow-y: clip` is not the escape it looks like. A `clip` beside a
 * scrolling axis computes to `hidden` — verified in the browser, not assumed —
 * so a strip that scrolls sideways CANNOT refuse to be scrolled downwards. The
 * discipline has to live in the code that scrolls it.
 *
 * The SLOT is measured, not the shape inside it, because the slot is the one
 * box in the chain that no motion grade transforms — measuring a mid-arrival
 * shape would scroll to where it is passing through rather than where it lands.
 */
function revealTab(button: HTMLButtonElement): void {
  const slot = button.closest<HTMLElement>("[data-tab-slot]");
  // Both ends are MARKED. Reaching the scroller as `slot.parentElement` would
  // have left half the chain positional: nest one wrapper and the scroll goes
  // to a div that does not scroll, silently, with nothing to notice it.
  const scroller = button.closest<HTMLElement>("[data-tab-scroller]");
  if (!slot || !scroller) return;
  const port = scroller.getBoundingClientRect();
  const box = slot.getBoundingClientRect();
  const before = port.left - (box.left - REVEAL_PADDING);
  const after = box.right + REVEAL_PADDING - port.right;
  if (before > 0) scroller.scrollLeft -= before;
  else if (after > 0) scroller.scrollLeft += after;
}

/**
 * Browser-style tab strip (PB-5176, Direction A). Data-blind — every
 * interaction fires a callback; the host store decides what to do. Sits inside
 * `TopBar`'s `tabsSlot`, whose bottom edge is the strip's baseline: tabs are
 * bottom-aligned 36px shapes with rounded tops, and the active one carries the
 * elevated fill, a three-side border and the module accent underline.
 *
 * Implemented as route navigation with roving-tabindex buttons:
 * Arrow/Home/End move focus, while Enter/Space use native button activation.
 * These controls navigate routes and do not own tabpanels, so using APG tab
 * roles would be semantically false. Actions and Close are sibling buttons,
 * never nested interactive descendants (`TabStripItem`).
 *
 * The strip scrolls horizontally without a visible scrollbar (the artboard's
 * call); keyboard reachability does not depend on it — roving focus reaches
 * every tab and the selected tab is scrolled into view when selection changes
 * from outside (deep link, close, programmatic navigation).
 *
 * See `packages/ui/CLAUDE.md` for the layering rules. Adding tab behaviour
 * (drag-reorder, restore, etc.) lands as new props + new callbacks, never as
 * a store inside this component.
 */
export function TabStrip({
  tabs,
  activeTabId,
  onTabClick,
  onTabClose,
  onTabCloseOthers,
  onTabTogglePin,
  onAddClick,
}: TabStripProps): ReactElement {
  // A tab is "closable" if it isn't pinned. Used to disable the "Close
  // other tabs" item when nothing else could be closed.
  const closableCount = tabs.filter((t) => !t.pinned).length;

  /* A closed tab collapses its own width instead of the strip snapping shut
     under the pointer (ADR-0091). The row is retained for its exit only —
     every count, index and callback above still reads `tabs`, so a leaving tab
     is scenery: it is not focusable, not navigable and not closable. */
  const { rows } = useListPresence(tabs, (tab) => tab.id);

  // Arrow/Home/End move focus between the route buttons; the focused route
  // carries tabIndex 0, the rest -1. Focus only moves on keyboard navigation,
  // never on parent-driven selection.
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  // Move DOM focus only when the change came from keyboard navigation — never
  // on mount or when selection syncs from outside (avoids focus-stealing).
  const navigatingRef = useRef(false);
  const selectedPosition = tabs.findIndex((t) => t.id === activeTabId);
  const selectedIndex = Math.max(0, selectedPosition);
  const onActiveChange = useCallback((i: number) => {
    if (!navigatingRef.current) return;
    const button = tabRefs.current[i];
    if (!button) return;
    // `focus()` would scroll only the button into view, leaving the tab's
    // controls and end flare clipped; reveal the whole tab instead.
    button.focus({ preventScroll: true });
    revealTab(button);
  }, []);
  const {
    activeIndex,
    setActiveIndex,
    onKeyDown: rovingKeyDown,
  } = useRovingTabIndex({
    itemCount: tabs.length,
    orientation: "horizontal",
    loop: true,
    initialIndex: selectedIndex,
    onActiveChange,
  });
  // Keep the roving anchor on the selected tab when selection changes from
  // outside (click, programmatic). navigatingRef is false here, so no focus move.
  useEffect(() => {
    setActiveIndex(selectedIndex);
  }, [selectedIndex, setActiveIndex]);

  // With the scrollbar hidden, nothing else would bring an off-screen tab back
  // into view when the host selects it — including the tab a New-tab click
  // just opened, which is why the earlier tabs move left to make room for it.
  useEffect(() => {
    if (selectedPosition === -1) return;
    const button = tabRefs.current[selectedPosition];
    if (button) revealTab(button);
  }, [activeTabId, selectedPosition]);

  return (
    // Positioned over its slot and pulled 1px past its bottom, so the tabs sit
    // ON the bar's bottom border: the active tab's base and flares cover the
    // border line the way a browser tab merges into the toolbar (d11). The
    // slot wrapper (`TopBar`, the stories) is `relative` for this reason.
    <nav
      aria-label="Open tabs"
      className="absolute inset-x-0 top-0 -bottom-px flex items-end gap-0.5 pe-2"
    >
      {/* Only the tab list scrolls (d12). It sizes to its tabs and shrinks
          before the New-tab control does, so `+` follows the last tab while
          the tabs fit and stays visible at the strip's end once they overflow,
          as in a browser — instead of scrolling away behind the utility
          cluster. 8px of padding at both ends, and the same scroll padding,
          keep an active tab's base flares (drawn 8px outside its box) clear of
          the clipped edge at rest and after a scroll-into-view. */}
      <div
        data-tab-scroller=""
        className="flex min-w-0 items-end gap-0.5 self-stretch overflow-x-auto overflow-y-hidden scroll-px-2 px-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        // Inline, not only the utility: theme.css sets `* { scrollbar-width: thin }`
        // in an UNLAYERED rule, which beats any layered utility regardless of
        // specificity — and in Chrome a set `scrollbar-width` also makes the
        // `::-webkit-scrollbar` hiding inert. Verified live before this line
        // existed: the computed value was still "thin".
        style={{ scrollbarWidth: "none" }}
      >
        {rows.map((row) => {
          const tab = row.item;
          /* Indices belong to the LIVE strip. A closing tab holds a slot on
             screen but none in the roving order, so arrow keys skip it and it
             can never take the focused tabIndex. */
          const i = tabs.findIndex((live) => live.id === tab.id);
          const canClose = !tab.pinned;
          // "Close other tabs" needs at least one other closable tab beyond
          // this one. If this tab itself is closable, subtract it from the
          // pool so a single closable tab doesn't enable the action on itself.
          const otherClosable = canClose ? closableCount - 1 : closableCount;
          const item = (
            <TabStripItem
              tab={tab}
              isActive={row.open && tab.id === activeTabId}
              tabIndex={row.open && i === activeIndex ? 0 : -1}
              buttonRef={(node) => {
                if (row.open) tabRefs.current[i] = node;
              }}
              onSelect={() => {
                if (!row.open) return;
                setActiveIndex(i);
                onTabClick?.(tab.id);
              }}
              onKeyDown={(e) => {
                if (!row.open) return;
                navigatingRef.current = true;
                rovingKeyDown(e);
                navigatingRef.current = false;
              }}
              canClose={row.open && canClose}
              canCloseOthers={row.open && otherClosable > 0}
              onClose={onTabClose}
              onCloseOthers={onTabCloseOthers}
              onTogglePin={onTabTogglePin}
              dividerBefore={i > 0 && tabs[i - 1]?.id !== activeTabId}
            />
          );
          /* ONE wrapper for every tab, open or leaving (ADR-0091). Rendering
             the collapse class only on the tab that is going would apply the
             grid and its closing state in the same frame, and a transition with
             no earlier value to start from simply snaps — which is exactly what
             the first cut of this did. `shrink-0` because the wrapper is now
             the flex item the tab shape used to be. */
          return (
            <div
              key={row.key}
              /* Two-way: a tab that reopens while it was leaving must become
                 reachable again. `inert` is imperative because React 18
                 serialises `inert={false}` as the truthy string "false". */
              ref={(node) => node?.toggleAttribute("inert", !row.open)}
              /* The slot is what `revealTab` measures: the one box here that no
                 motion grade transforms. Marked rather than found by position,
                 so nesting another wrapper cannot silently change what the
                 scroll is aimed at. */
              data-tab-slot=""
              className="pb-exit-col shrink-0"
              data-motion-state={row.open ? "open" : "closing"}
              {...(row.open ? {} : { "aria-hidden": "true" as const })}
            >
              {/* `pb-enter-col`, not `pb-enter-row`: the row grade lifts an
                  element through the BLOCK axis, and this strip's block axis is
                  the one it clips rather than scrolls. A tab grows into place
                  instead. */}
              <div className="pb-enter-col">{item}</div>
            </div>
          );
        })}
      </div>

      {/* The launcher entry point: a 32px square after the tab list. The nav is
          1px taller than the bar because its active-tab baseline overlaps the
          border, so self-centering plus a 1px optical correction aligns this
          control with the brand and utility controls instead of the lower tab
          centre line. */}
      <Tooltip
        content="New tab"
        describeChild={false}
        wrapperClassName="shrink-0 -translate-y-px self-center"
      >
        <ChromeIconButton
          onClick={onAddClick}
          aria-label="New tab"
          /* A stable semantic handle for the onboarding tour. Marked here rather
             than matched by class or position, so restyling this control cannot
             silently leave a tour pointing at nothing. */
          data-tour-id="shell.new-tab"
        >
          <Plus className="h-4 w-4" />
        </ChromeIconButton>
      </Tooltip>
    </nav>
  );
}
